import { toFormattedDate } from 'components/DateTime';
import { toMoney } from 'components/Money';
import { formatPhone } from 'components/Phone';
import { isRichTextEmpty } from 'helpers/richText';
import { isJSON } from 'libs/util/json';
/**
 * @function getOptions
 * @since 1.0.0
 * @hidden
 */
export function getOptions(options, language) {
    var _a;
    if (!options) {
        return [];
    }
    let choices = (_a = JSON.parse(options)) === null || _a === void 0 ? void 0 : _a.choices;
    if (choices) {
        choices = choices.map((choice) => {
            let { label, value, image } = choice;
            if (label instanceof Array) {
                let match = null;
                if (match == null)
                    match = label.find((entry) => entry.language == language);
                if (match == null)
                    match = label[0];
                if (match) {
                    label = match.label;
                }
                else {
                    label = t('Unlabeled');
                }
            }
            return {
                label,
                value,
                image
            };
        });
        return choices.sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
}
/**
 * @function getFieldValueLabel
 * @since 1.0.0
 */
export function getFieldValueLabel(value, field, language) {
    // if value is an JSON string, parse it
    if (isJSON(value)) {
        const isObject = typeof JSON.parse(value) === 'object';
        if (isObject)
            value = JSON.parse(value).value ? JSON.parse(value).value : value;
    }
    const options = getOptions(field.options, language);
    if (typeof value === 'object') {
        // if not an array, get the first key
        if (!Array.isArray(value))
            value = Object.keys(value)[0];
    }
    // TODO - Fix this (some value is an empty string)
    if (isJSON(value)) {
        value =
            typeof JSON.parse(value) == 'object' ? JSON.parse(value).value : value;
    }
    if (Array.isArray(value)) {
        return value
            .map((v) => {
            return options.find((o) => o.value == v)
                ? options.find((o) => o.value == v).label
                : v;
        })
            .join(', ');
    }
    return options.find((o) => o.value == value)
        ? options.find((o) => o.value == value).label
        : value;
}
/**
 * @function getFieldValue
 * @since 1.0.0
 */
export function getFieldValue(value, language) {
    if (value.translations) {
        return value.translations[language];
    }
    return value.value;
}
/**
 * @function parseTranslatableFieldEnvelope
 * @since 1.0.0
 */
export function parseTranslatableFieldEnvelope(raw) {
    var _a, _b, _c, _d;
    if (raw == null || raw === '') {
        return { value: '', translations: {} };
    }
    if (typeof raw === 'object') {
        const envelope = raw;
        return {
            value: (_a = envelope.value) !== null && _a !== void 0 ? _a : '',
            translations: (_b = envelope.translations) !== null && _b !== void 0 ? _b : {}
        };
    }
    if (typeof raw !== 'string') {
        return { value: String(raw), translations: {} };
    }
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
            return {
                value: (_c = parsed.value) !== null && _c !== void 0 ? _c : '',
                translations: (_d = parsed.translations) !== null && _d !== void 0 ? _d : {}
            };
        }
    }
    catch (_e) {
        return { value: raw, translations: {} };
    }
    return { value: raw, translations: {} };
}
/**
 * @function parseTranslatableFieldDefaultForForm
 * @since 1.0.0
 */
export function parseTranslatableFieldDefaultForForm(defaultValue, languages) {
    const envelope = parseTranslatableFieldEnvelope(defaultValue);
    return languages.map((language) => {
        var _a, _b, _c;
        return ({
            defaultValue: (_c = (_b = (_a = envelope.translations) === null || _a === void 0 ? void 0 : _a[language.code]) !== null && _b !== void 0 ? _b : envelope.value) !== null && _c !== void 0 ? _c : ''
        });
    });
}
/**
 * @function serializeTranslatableFieldDefault
 * @since 1.0.0
 */
export function serializeTranslatableFieldDefault(translationsForm, languages) {
    var _a;
    if (!translationsForm) {
        return '';
    }
    const translations = {};
    let primaryValue = '';
    languages.forEach((language, index) => {
        var _a;
        const entry = Array.isArray(translationsForm)
            ? translationsForm[index]
            : translationsForm[index];
        const content = (_a = entry === null || entry === void 0 ? void 0 : entry.defaultValue) !== null && _a !== void 0 ? _a : '';
        if (content) {
            translations[language.code] = content;
        }
        if (language.primary) {
            primaryValue = content;
        }
    });
    if (!primaryValue && languages.length > 0) {
        const firstEntry = Array.isArray(translationsForm)
            ? translationsForm[0]
            : translationsForm[0];
        primaryValue = (_a = firstEntry === null || firstEntry === void 0 ? void 0 : firstEntry.defaultValue) !== null && _a !== void 0 ? _a : '';
    }
    if (!primaryValue && Object.keys(translations).length === 0) {
        return '';
    }
    return JSON.stringify({ value: primaryValue, translations });
}
/**
 * @function getFilledFieldValues
 * @since 1.0.0
 */
export function getFilledFieldValues(fields) {
    if (!fields) {
        return {};
    }
    return Object.fromEntries(Object.entries(fields).filter(([, value]) => {
        if (value == null || value === '')
            return false;
        if (Array.isArray(value) && value.length === 0)
            return false;
        return true;
    }));
}
/**
 * @function formatFieldValueWithTranslation
 * @since 1.0.0
 */
export function formatFieldValueWithTranslation(values, translations, language, languages) {
    let normalFields = [];
    const translationFields = [];
    // Build normal fields
    if (values) {
        // Handle both array and object formats
        const valuesObject = Array.isArray(values)
            ? values.reduce((acc, fieldValue) => {
                if (fieldValue && fieldValue.field && fieldValue.field.name) {
                    acc[fieldValue.field.name] = fieldValue.value;
                }
                return acc;
            }, {})
            : values;
        if (valuesObject && typeof valuesObject === 'object' && !Array.isArray(valuesObject)) {
            normalFields = Object.entries(valuesObject).map((field) => {
                const [name, value] = field;
                return {
                    fieldId: name,
                    value: JSON.stringify({ value })
                };
            });
        }
    }
    // Build translations
    if (translations) {
        languages.forEach((lang, index) => {
            Object.entries(translations[index].fields).map((field) => {
                const [name, value] = field;
                // Check if field exists
                const fieldExists = translationFields.find((field) => field.fieldId == name);
                if (fieldExists) {
                    fieldExists.translations.push({
                        language: lang.code,
                        title: value
                    });
                    return;
                }
                translationFields.push({
                    fieldId: name,
                    value: JSON.stringify({ value }),
                    translations: [
                        {
                            language: lang.code,
                            title: value
                        }
                    ]
                });
            });
        });
    }
    return [...normalFields, ...translationFields];
}
/**
 * @function formatFieldValue
 * @since 1.0.0
 */
export function formatFieldValue(value, type, field, language = 'fr', arrayOrObject = false, functionValue) {
    // Parse value and check for translations
    const parsedValue = typeof value === 'string'
        ? JSON.parse(value)
        : typeof value === 'object' && value !== null
            ? value
            : value;
    // For translatable fields (TEXTBOX, TEXTAREA, RICH_TEXT), check if translations exist
    const translatableTypes = ['TEXTBOX', 'TEXTAREA', 'RICH_TEXT'];
    if (translatableTypes.includes(type) &&
        typeof parsedValue === 'object' &&
        (parsedValue === null || parsedValue === void 0 ? void 0 : parsedValue.translations)) {
        // If translations exist, return the value in the specified language
        const translatedValue = parsedValue.translations[language];
        if (translatedValue !== undefined && translatedValue !== null) {
            return translatedValue;
        }
    }
    // Extract the actual value
    const v = typeof parsedValue === 'object' &&
        parsedValue !== null &&
        'value' in parsedValue
        ? parsedValue.value
        : parsedValue;
    if (v === null) {
        return null;
    }
    if (type === 'DATE') {
        return toFormattedDate(v, { y: 'numeric', m: 'numeric', d: 'numeric' });
    }
    else if (type === 'TIME') {
        return toFormattedDate(v, {
            hh: 'numeric',
            mm: 'numeric',
            date: false,
            time: true
        });
    }
    else if (type === 'YEAR') {
        return toFormattedDate(v, { date: false, time: false, yearOnly: true });
    }
    else if (type === 'DATETIME') {
        return toFormattedDate(v, {
            y: 'numeric',
            m: 'numeric',
            d: 'numeric',
            hh: 'numeric',
            mm: 'numeric',
            time: true
        });
    }
    else if (type === 'DATE_RANGE') {
        const [startDate, endDate] = v.split(',');
        return `${toFormattedDate(startDate, {
            y: 'numeric',
            m: 'numeric',
            d: 'numeric'
        })} - ${toFormattedDate(endDate, {
            y: 'numeric',
            m: 'numeric',
            d: 'numeric'
        })}`;
    }
    else if (type === 'SELECT_DROPDOWN') {
        if (field && field.options) {
            const options = getOptions(field.options, language);
            const option = options.find((o) => o.value == v);
            if (option) {
                return option.label;
            }
        }
        return v;
    }
    else if (type === 'MULTISELECT') {
        if (field && field.options) {
            const options = getOptions(field.options, language);
            const selectedOptions = options.filter((o) => v.includes(o.value));
            if (arrayOrObject) {
                return selectedOptions.map((o) => o.label);
            }
            return selectedOptions.map((o) => o.label).join(', ');
        }
    }
    else if (type === 'YESNO') {
        const isYes = v === true || v === 'true' || v === '1' || v === 'YES';
        return isYes ? t('Yes') : t('No');
    }
    else if (type === 'FUNCTION_AVERAGE') {
        if (!functionValue)
            return v;
        return toMoney(Number(functionValue) / v);
    }
    else if (type === 'PHONE') {
        return formatPhone(v);
    }
    else if (type === 'MONEY') {
        return toMoney(Number(v));
    }
    return v;
}
const TRANSLATABLE_FIELD_TYPES = ['TEXTBOX', 'TEXTAREA', 'RICH_TEXT'];
/**
 * Custom field values are JSON (`{"value": ...}`), sometimes with translations.
 * A viewer row is shown only when that stored value would display something.
 * Formatting alone is not enough: an empty yes/no becomes "No", and an empty
 * amount becomes a zero.
 */
export function hasDisplayableCustomFieldValue(value, field, language = 'fr') {
    var _a;
    const type = (_a = field === null || field === void 0 ? void 0 : field.type) !== null && _a !== void 0 ? _a : '';
    const stored = readStoredCustomFieldValue(value, type, language);
    if (isBlankCustomFieldValue(stored, type)) {
        return false;
    }
    try {
        return !isBlankCustomFieldValue(formatFieldValue(value, type, field, language, true), type);
    }
    catch (_b) {
        return false;
    }
}
function readStoredCustomFieldValue(value, type, language) {
    let parsed = value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === '{}') {
            return null;
        }
        try {
            parsed = JSON.parse(trimmed);
        }
        catch (_a) {
            return trimmed;
        }
    }
    if (parsed == null || typeof parsed !== 'object') {
        return parsed;
    }
    const record = parsed;
    if (TRANSLATABLE_FIELD_TYPES.includes(type) && record.translations) {
        const translatedValue = record.translations[language];
        if (translatedValue !== undefined && translatedValue !== null) {
            return translatedValue;
        }
    }
    if ('value' in record) {
        return record.value;
    }
    return null;
}
function isBlankCustomFieldValue(value, type) {
    if (value == null) {
        return true;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return false;
    }
    if (Array.isArray(value)) {
        return (value.length === 0 ||
            value.every((entry) => isBlankCustomFieldValue(entry, type)));
    }
    if (typeof value === 'string') {
        if (value.trim() === '' || value.trim() === 'null') {
            return true;
        }
        if (TRANSLATABLE_FIELD_TYPES.includes(type)) {
            return isRichTextEmpty(value);
        }
        return false;
    }
    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }
    return false;
}
